import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CompareContext, MAX_COMPARE } from './CompareContext.js';

const STORAGE_KEY = 'bs_compare';

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item.slug === 'string')
      .slice(0, MAX_COMPARE);
  } catch {
    return [];
  }
}

/**
 * Selected programmes for the compare tray. Persisted in localStorage so the
 * selection survives navigation between home, university and course pages.
 * Each item: { slug, name, universityName, universitySlug, levelCode }.
 */
export default function CompareProvider({ children }) {
  const [items, setItems] = useState(readStored);
  const [limitMessage, setLimitMessage] = useState(null);
  const messageTimer = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // storage unavailable; the selection simply will not persist
    }
  }, [items]);

  useEffect(() => {
    function onStorage(event) {
      if (event.key === STORAGE_KEY) {
        setItems(readStored());
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => () => window.clearTimeout(messageTimer.current), []);

  const has = useCallback((slug) => items.some((item) => item.slug === slug), [items]);

  const remove = useCallback((slug) => {
    setItems((prev) => prev.filter((item) => item.slug !== slug));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const toggle = useCallback(
    (item) => {
      if (items.some((existing) => existing.slug === item.slug)) {
        setItems((prev) => prev.filter((existing) => existing.slug !== item.slug));
        return { ok: true, added: false };
      }
      if (items.length >= MAX_COMPARE) {
        setLimitMessage(`You can compare up to ${MAX_COMPARE} programmes. Remove one to add another.`);
        window.clearTimeout(messageTimer.current);
        messageTimer.current = window.setTimeout(() => setLimitMessage(null), 4000);
        return { ok: false, added: false };
      }
      setItems((prev) => [...prev, item]);
      return { ok: true, added: true };
    },
    [items]
  );

  const value = useMemo(
    () => ({ items, has, toggle, remove, clear, limitMessage }),
    [items, has, toggle, remove, clear, limitMessage]
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
}
